import * as React from "react";
import { Plus, X } from "lucide-react";

import { AmountInput } from "@/components/admin/AmountInput";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/Combobox";
import { Label } from "@/components/ui/label";
import { CURRENCIES } from "@/lib/countries";
import { cn } from "@/lib/utils";
import { AdvanceAmount } from "../../types";

/** An advance's money as a small table of rows - one currency + amount per
 *  row - shared by the employee's Request Advance form (MyRequests.tsx) and
 *  an approver's ReviewAdvanceDialog.
 *
 *  The first row is always the trip's POLICY currency (fixed, can't be
 *  removed). No row - including this one - is capped or currency-restricted
 *  any more: an advance can be requested for any amount, in any currency
 *  (confirmed desired behaviour - an employee can need more than the
 *  policy's indicative cap, or receive it in a currency the policy doesn't
 *  configure for this trip, and Finance reconciles the actual amount by
 *  hand regardless). The policy limit is still shown under the policy row
 *  (`limitLine`, turning red once exceeded) purely as an informational
 *  heads-up, never a block - see requests_api.py's _clean_advance_amounts,
 *  which no longer enforces `caps` either. Each currency can still only
 *  appear once. "Add amount" adds further free "also received" rows, any
 *  currency. */
export function AdvanceAmountsField({
  value,
  onChange,
  policyCurrency,
  limitsByCurrency,
  limitHint,
  allowedCurrencies,
  idPrefix
}: {
  value: AdvanceAmount[];
  onChange: (value: AdvanceAmount[]) => void;
  /** The trip's policy currency ("" until a country is chosen). */
  policyCurrency: string;
  /** Each currency's cap for this trip, or null while unknown (the
   *  approver's Review dialog has none client-side - the server checks). */
  limitsByCurrency: Record<string, number> | null;
  /** Shown under each row instead of its limit while the limits are
   *  loading / can't be computed yet (the caller's own wording). */
  limitHint?: React.ReactNode;
  /** Currencies policy configures for this employee + claim method + trip
   *  type. */
  allowedCurrencies: string[];
  idPrefix: string;
}) {
  // Keep a row for the policy currency at all times once it's known - on a
  // fresh form, and when the trip's country changes to one in a different
  // currency (the old row then stays as an extra row the employee can
  // remove, or fix if its currency is no longer allowed).
  React.useEffect(() => {
    if (policyCurrency && !value.some((l) => l.currency === policyCurrency)) {
      onChange([{ currency: policyCurrency, amount: 0 }, ...value]);
    }
  }, [policyCurrency, value, onChange]);

  const policyRow = value.find((l) => l.currency === policyCurrency);
  const extraRows = value.filter((l) => l.currency !== policyCurrency);
  const used = new Set(value.map((l) => l.currency));
  // The policy row's own picker (not rendered here - it's fixed, see the
  // default row below) stays restricted to allowedCurrencies; an extra
  // "also received" row can be ANY currency (see this file's own top
  // comment) - CURRENCIES is the same full list CurrencyConversionCard's
  // settlement-currency picker already uses.
  const unusedAny = CURRENCIES.filter((c) => !used.has(c));

  const replace = (currency: string, line: AdvanceAmount) =>
    onChange(value.map((l) => (l.currency === currency ? line : l)));
  const remove = (currency: string) =>
    onChange(value.filter((l) => l.currency !== currency));
  const addRow = () => {
    if (unusedAny.length === 0) return;
    onChange([...value, { currency: unusedAny[0], amount: 0 }]);
  };

  // Under each row: its own limit (red once exceeded), or the caller's hint
  // while limits aren't known yet.
  const limitLine = (line: AdvanceAmount) => {
    const limit = limitsByCurrency?.[line.currency];
    if (limit === undefined) return limitHint ?? null;
    return (
      <p
        className={cn(
          "text-xs",
          line.amount > limit ? "text-destructive" : "text-muted-foreground"
        )}
      >
        Policy limit for this trip: {line.currency} {limit.toLocaleString()}
      </p>
    );
  };

  if (!policyCurrency) {
    return (
      <div className="space-y-1.5">
        <Label>Amount</Label>
        <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          Choose the claim method, trip type and country first - the amount
          is requested in that trip's policy currency.
        </p>
      </div>
    );
  }

  const row = "grid grid-cols-[6.5rem_minmax(0,1fr)_2.25rem] items-center gap-2";

  return (
    <div className="space-y-2">
      <div className={cn(row, "text-sm font-medium")}>
        <span>Currency</span>
        <span>Amount</span>
        <span className="sr-only">Remove</span>
      </div>

      {/* Default row - the trip's own policy currency. */}
      <div className="space-y-1">
        <div className={row}>
          <div className="flex h-10 items-center rounded-md border bg-muted/50 px-3 text-sm font-medium">
            {policyCurrency}
          </div>
          <AmountInput
            id={`${idPrefix}-amount-${policyCurrency}`}
            value={policyRow?.amount ?? 0}
            onChange={(amount) =>
              replace(policyCurrency, { currency: policyCurrency, amount })
            }
          />
          <span />
        </div>
        {limitLine(policyRow ?? { currency: policyCurrency, amount: 0 })}
      </div>

      {extraRows.map((line) => (
        <div key={line.currency} className="space-y-1">
          <div className={row}>
            <Combobox
              value={line.currency}
              onChange={(currency) => replace(line.currency, { ...line, currency })}
              options={[line.currency, ...unusedAny].map((c) => ({ value: c, label: c }))}
              placeholder="Currency…"
              searchPlaceholder="Search…"
              className="h-10"
            />
            <AmountInput
              id={`${idPrefix}-amount-${line.currency}`}
              value={line.amount}
              onChange={(amount) => replace(line.currency, { ...line, amount })}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              aria-label={`Remove ${line.currency}`}
              onClick={() => remove(line.currency)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {/* No policy limit/"not allowed" messaging here - an extra row is
             a free "also received" line, any currency, no cap (see this
             file's own top comment). */}
          <p className="text-xs text-muted-foreground">
            Also received - any currency, reconciled by Finance.
          </p>
        </div>
      ))}

      <Button type="button" variant="outline" size="sm" onClick={addRow}>
        <Plus className="mr-1.5 h-3.5 w-3.5" />
        Add amount
      </Button>
    </div>
  );
}

/** Client-side mirror of the backend's _clean_advance_amounts - returns the
 *  first problem, or null. No amount limit is enforced here (or server-side)
 *  for any row, including the policy-currency one - the policy limit is
 *  shown only as an informational red warning under the row (see
 *  AdvanceAmountsField's limitLine above), never a block. `limitsByCurrency`/
 *  `allowedCurrencies`/`policyCurrency` are accepted for callers that still
 *  pass them (unused now) rather than changing every call site. */
export function advanceAmountsError(
  amounts: AdvanceAmount[],
  _limitsByCurrency: Record<string, number> | null,
  _allowedCurrencies: string[] = [],
  _policyCurrency?: string
): string | null {
  if (amounts.length === 0) return "Enter an amount.";
  const missing = amounts.find((a) => !(a.amount > 0));
  if (missing) return `Enter an amount greater than 0 for ${missing.currency}.`;
  return null;
}
